import { db } from '../../../firebase';
import { doc, setDoc, updateDoc, arrayUnion, deleteDoc } from 'firebase/firestore';
import dayjs from 'dayjs';
import { sendEmail } from "../../../functions/endpoints/SendEmail";

export const completeEnrollment = async ({
  user,
  formData,
  consentData,
  timestamp,
  customerId,
  transId,
  paymentResult,
  cardDetails,
  cardId
}) => {
  const writtenCollections = [];
  try {
    const normalizedEmail = formData.email.toLowerCase().trim();

    // Create practitioner record
    const practitionerData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: normalizedEmail,
      phone: formData.phone,
      businessName: formData.businessName.trim() || null,
      dateCreated: timestamp,
      dateModified: timestamp,
      completedOnboarding: false,
      squareId: customerId,
      hasSelfClient: true, // Set hasSelfClient
      training: {},
      onboarding: {
        cardOnFile: { dateCompleted: timestamp } // Set cardOnFile
      },
      consent: {
        agreed: consentData.agreed,
        signature: consentData.signature.trim(),
        dateSigned: timestamp,
        version: '20250528'
      }
    };
    await setDoc(doc(db, 'practitioners', user.uid), practitionerData);
    writtenCollections.push('practitioners');

    // Create user record
    const userData = {
      email: normalizedEmail,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      businessName: formData.businessName.trim(),
      role: 'practitioner'
    };
    await setDoc(doc(db, 'users', user.uid), userData);
    writtenCollections.push('users');

    // Update management
    const managementData = {
      email: normalizedEmail,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      id: user.uid,
      businessName: formData.businessName.trim() || null,
      onboarding: ['cardOnFile'] // Add cardOnFile to onboarding array
    };
    const managementRef = doc(db, 'app', 'management');
    await updateDoc(managementRef, { practitioners: arrayUnion(managementData) });

    // Create transaction with promo if applied
    const currentYear = dayjs(timestamp.toDate()).year().toString();
    const transRef = doc(db, `practitioners/${user.uid}/transactions`, currentYear);
    const transData = {
      id: transId,
      type: 'subscription-initiated',
      dateCreated: timestamp,
      amount: paymentResult.data.amount,
      card: paymentResult.data.card
    };
    if (paymentResult.data.promoCode) {
      transData.promoCode = paymentResult.data.promoCode;
      transData.discount = paymentResult.data.discount;
    }
    await setDoc(transRef, {
      trans: [transData]
    }, { merge: true });

    // Create billing
    await setDoc(doc(db, 'billing', user.uid), {
      cards: [{
        cardId,
        brand: cardDetails.brand,
        enabled: true,
        expMonth: cardDetails.expMonth,
        expYear: cardDetails.expYear,
        last4: cardDetails.last4,
        storeCardAgreement: true,
        type: 'business',
        agreementText: "I authorize this business to keep my credit card on file for any future transactions until I request a cancellation. I understand that the terms of this authorization will remain in effect until I request that they be terminated. I acknowledge that I am responsible for making this request in writing.",
        dateSigned: timestamp,
        dateAdded: timestamp,
        dateModified: timestamp
      }]
    });
    writtenCollections.push('billing');

    // Create transactions record with promo if applied
    const transactionData = {
      amount: paymentResult.data.amount,
      card: paymentResult.data.card,
      createdAt: timestamp,
      customerId,
      orderId: paymentResult.data.orderId,
      paymentDate: timestamp,
      paymentId: transId,
      status: 'completed',
      type: 'subscription-initiated',
      updatedAt: timestamp
    };
    if (paymentResult.data.promoCode) {
      transactionData.promoCode = paymentResult.data.promoCode;
      transactionData.discount = paymentResult.data.discount;
    }
    await setDoc(doc(db, 'transactions', transId), transactionData);
    writtenCollections.push('transactions');

    // Create self-client record
    const clientData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: normalizedEmail,
      dob: formData.dob ? dayjs(formData.dob).format('YYYY-MM-DD') : null,
      street: formData.street.trim(),
      address2: formData.address2.trim() || '',
      city: formData.city.trim(),
      state: formData.state,
      zip: formData.zip.trim(),
      hasUsedFreeScan: false,
      immediateFamily: true,
      concerns: '',
      dateCreated: timestamp,
      dateModified: timestamp,
      practitionerId: user.uid,
      scans: []
    };
    await setDoc(doc(db, 'clients', user.uid), clientData);
    writtenCollections.push('clients');

    // Update practitioners/{user.uid}/clients/list with self-client
    const clientEntry = {
      id: user.uid,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: normalizedEmail
    };
    const listRef = doc(db, "practitioners", user.uid, "clients", "list");
    await setDoc(listRef, {
      clients: arrayUnion(clientEntry)
    }, { merge: true });

    // Update clientMap
    const clientMapEntry = {
      firstName: formData.firstName.trim(),
      id: user.uid,
      lastName: formData.lastName.trim(),
      pId: user.uid
    };
    await updateDoc(doc(db, 'app', 'clientMap'), {
      clients: arrayUnion(clientMapEntry)
    });

    // Send Email
    if (!practitionerData) {
      throw new Error("Practitioner data not available.");
    }
    const practitionerName = `${practitionerData.firstName} ${practitionerData.lastName}`;
    const emailData = {
      recipientEmail: process.env.REACT_APP_ENVIRONMENT === 'PROD' ? 'hello@trubalancebioenergetics.com' : "jhatlestad@gmail.com",
      recipientName: "TruBalance Admin",
      subject: "New Practitioner Registration",
      templateId: "practitioner-registered",
      dynamicTemplateData: {
        practitionerName,
        practitionerEmail: practitionerData.email
      },
      emailType: "practitioner_registered",
      contextData: {
        practitionerId: user.uid
      },
      userId: user.uid,
      isTest: false
    };

    sendEmail(emailData);

    // Add practitioner to FloDesk segment (only in production)
    if (process.env.NODE_ENV === 'production') {
      const flodeskPayload = {
        email: normalizedEmail,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        double_optin: false, // Skip double opt-in for seamless updates
        segment_ids: [process.env.REACT_APP_FLODESK_SEGMENT_ACCOUNT_CREATION_ID]
      };

      try {
        const apiKey = process.env.REACT_APP_FLODESK_API_KEY;
        if (!apiKey) {
          throw new Error("Missing REACT_APP_FLODESK_API_KEY");
        }
        const base64Credentials = btoa(`${apiKey}:`);
        const response = await fetch('https://api.flodesk.com/v1/subscribers', {
          method: 'POST',
          headers: {
            Authorization: `Basic ${base64Credentials}`,
            'Content-Type': 'application/json',
            'User-Agent': 'TruBalance Bioenergetics (www.trubio.app)'
          },
          body: JSON.stringify(flodeskPayload)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
      } catch (error) {
        console.error('Error creating FloDesk subscriber:', {
          error: error.message,
          status: error.status
        });
        // Continue execution to avoid failing the enrollment
      }
    }
  } catch (err) {
    // Rollback on error
    for (const collection of writtenCollections) {
      await deleteDoc(doc(db, collection, user.uid));
    }
    await user.delete();
    throw err;
  }
};